import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { ApiKeysSection } from "../components/ApiKeysSection";
import { Copy, Play, Terminal, BookOpenText, Loader2, ChevronDown, ChevronRight } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const ENDPOINTS = [
  {
    group: "Müşteri / Proje Bazlı",
    items: [
      {
        path: "/customer-products", name: "Müşteri Bazında Ürün Özeti",
        desc: "Her müşterinin (örn. Samsun Belediyesi) sipariş ettiği ürünler: resim, adet, birim fiyat, tutar ve para birimi toplamları. Manuel eklenen miktarlar manualQty alanında ayrışır.",
        params: [{ name: "organisation", desc: "Müşteri adı ile filtrele (opsiyonel, birebir eşleşme)" }],
        fields: "organisation, orderCount, orderRefs, totals{PARA: tutar}, products[{sku, name, variant, image, qty, unit, unitPrice, amount, currency, manualQty, refs}]",
      },
      {
        path: "/customer-orders", name: "Sipariş Sipariş Detaylı Liste",
        desc: "Her sipariş kalemleriyle birlikte; kalemlerde ürün görseli URL'i yer alır. source alanı portal / manual kaynağını ayırır.",
        params: [{ name: "organisation", desc: "Müşteri adı ile filtrele (opsiyonel, birebir eşleşme)" }],
        fields: "ref, organisation, source(portal|manual), status, zone, space, placedAt, subtotal, vatAmount, total, lines[{sku, name, variant, qty, unit, unitPrice, lineTotal, currency, image}]",
      },
    ],
  },
  {
    group: "Siparişler",
    items: [
      {
        path: "/orders", name: "Tüm Siparişler (Ham)",
        desc: "Portal + manuel tüm siparişler birleşik. Manuel kayıtlar manual: true ve MAN- referansı ile ayrışır.",
        params: [], fields: "ref, organisation, status, zone, space{label,hall,plot}, placedAt, currency, subtotal, vatAmount, total, lines[...]",
      },
      {
        path: "/orders/by-product", name: "Ürün Bazında Özet",
        desc: "Ürün+varyant bazında toplam talep, satınalınan, teslim edilen miktarlar ve hedef (orderers) dağılımı.",
        params: [], fields: "category, products[{sku, variant, qty, procuredQty, deliveredQty, manualQty, orderers[], supplierBreakdown[], deliveryEntries[]}]",
      },
      {
        path: "/manual-orders", name: "Manuel Siparişler",
        desc: "Sadece bu sisteme manuel eklenen siparişler (ham kayıt).",
        params: [], fields: "id, ref, organisation, zone, space_type(stand|pavilion|office), space_no, lines[], created_at",
      },
    ],
  },
  {
    group: "Katalog & Medya",
    items: [
      {
        path: "/products", name: "Ürün Kataloğu",
        desc: "Tüm ürünler; fiyat kademeleri, kategori, varyantlar ve görsel yolları.",
        params: [], fields: "sku, name, nameTr, category, subCategory, unit, kind, price{early|standard|late}, currency, images[], updatedAt",
      },
      {
        path: "/media/{dosya-yolu}", name: "Ürün Görseli", binary: true,
        desc: "Ürün görselini döner (PNG/JPEG). customer-products ve customer-orders yanıtlarındaki image alanı doğrudan bu uca işaret eden hazır URL'dir; aynı X-API-Key başlığıyla çağrılır.",
        params: [], fields: "İkili (binary) görsel içerik — JSON değil",
      },
    ],
  },
  {
    group: "Tedarik & Teslimat",
    items: [
      {
        path: "/procurement", name: "Satınalma Kayıtları",
        desc: "Satınalma işaretleri: hangi kalem hangi tedarikçiye ne miktarla atandı.",
        params: [], fields: "_id, key, sku, ref, variant, qty, organisation, supplier_id, supplier_name, direct, marked_at",
      },
      {
        path: "/deliveries", name: "Mal Kabul Kayıtları",
        desc: "Fiziksel teslimat girişleri (sku + varyant bazında).",
        params: [], fields: "_id, sku, variant, qty, date, status, supplier_id, entered_by, accepted_at",
      },
    ],
  },
];

const EndpointCard = ({ ep, apiKey }) => {
  const [open, setOpen] = useState(false);
  const [paramVal, setParamVal] = useState("");
  const [trying, setTrying] = useState(false);
  const [result, setResult] = useState(null);
  const slug = ep.path.replace(/[/{}]/g, "-").replace(/^-|-$/g, "");

  const url = useMemo(() => {
    let u = `${BASE}/api/ext/v1${ep.path}`;
    if (ep.params.length && paramVal.trim()) u += `?${ep.params[0].name}=${encodeURIComponent(paramVal.trim())}`;
    return u;
  }, [ep, paramVal]);

  const copyText = (t, msg) => navigator.clipboard.writeText(t).then(() => toast.success(msg)).catch(() => toast.error("Kopyalanamadı"));
  const curl = `curl -H "X-API-Key: ${apiKey || "<ANAHTAR>"}" "${url}"`;

  const tryIt = async () => {
    if (!apiKey) return toast.error("Önce yukarıdan bir API anahtarı seçin");
    setTrying(true);
    setResult(null);
    try {
      const r = await fetch(url, { headers: { "X-API-Key": apiKey } });
      const text = await r.text();
      let pretty = text;
      let count = null;
      try {
        const j = JSON.parse(text);
        count = Array.isArray(j.data) ? j.data.length : null;
        pretty = JSON.stringify(j, null, 2);
      } catch { /* binary or non-json */ }
      setResult({ status: r.status, count, body: pretty.slice(0, 5000), truncated: pretty.length > 5000 });
    } catch (e) {
      setResult({ status: "HATA", count: null, body: String(e), truncated: false });
    } finally {
      setTrying(false);
    }
  };

  return (
    <div className="border border-stone-200 bg-white" data-testid={`api-endpoint-card-${slug}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-stone-50" data-testid={`api-endpoint-toggle-${slug}`}>
        {open ? <ChevronDown size={14} className="text-stone-400" /> : <ChevronRight size={14} className="text-stone-400" />}
        <span className="rounded-sm bg-[#1C4E35] px-1.5 py-0.5 text-[10px] font-bold text-white">GET</span>
        <code className="text-xs font-semibold text-[#0A0D0B]">/api/ext/v1{ep.path}</code>
        <span className="ml-auto hidden text-xs text-stone-500 sm:block">{ep.name}</span>
      </button>
      {open && (
        <div className="border-t border-stone-100 px-4 py-3">
          <p className="text-xs text-stone-600">{ep.desc}</p>
          {ep.params.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {ep.params.map((p) => (
                <span key={p.name} className="text-[11px] text-stone-500"><code className="bg-stone-100 px-1">{p.name}</code> — {p.desc}</span>
              ))}
              <Input value={paramVal} onChange={(e) => setParamVal(e.target.value)} placeholder={`${ep.params[0].name} değeri (örn. Samsun Belediyesi)`} className="h-7 w-64 rounded-sm border-stone-300 text-xs" data-testid={`api-param-${slug}`} />
            </div>
          )}
          <p className="mt-2 text-[11px] text-stone-500"><span className="font-semibold">Yanıt alanları:</span> <code className="break-all bg-stone-50 px-1">{ep.fields}</code></p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => copyText(url, "URL kopyalandı")} className="flex items-center gap-1 border border-stone-300 px-2 py-1 text-[11px] font-semibold text-stone-700 hover:bg-stone-100" data-testid={`api-copy-url-${slug}`}>
              <Copy size={11} /> URL Kopyala
            </button>
            <button type="button" onClick={() => copyText(curl, apiKey ? "curl komutu kopyalandı" : "curl kopyalandı — anahtar seçilmediği için <ANAHTAR> yer tutucusu eklendi")} className="flex items-center gap-1 border border-stone-300 px-2 py-1 text-[11px] font-semibold text-stone-700 hover:bg-stone-100" data-testid={`api-copy-curl-${slug}`}>
              <Terminal size={11} /> curl Kopyala
            </button>
            {!ep.binary && (
              <button type="button" onClick={tryIt} disabled={trying} className="flex items-center gap-1 bg-[#1C4E35] px-2.5 py-1 text-[11px] font-bold text-white hover:bg-[#143A26] disabled:opacity-50" data-testid={`api-try-${slug}`}>
                {trying ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />} Dene
              </button>
            )}
          </div>
          {result && (
            <div className="mt-3" data-testid={`api-try-result-${slug}`}>
              <p className="text-[11px] font-semibold text-stone-600">
                HTTP {result.status}{result.count !== null ? ` · ${result.count} kayıt` : ""}{result.truncated ? " · yanıt kısaltıldı (ilk 5000 karakter)" : ""}
              </p>
              <pre className="mt-1 max-h-72 overflow-auto border border-stone-200 bg-stone-900 p-3 text-[11px] leading-relaxed text-emerald-200">{result.body}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function ApiPage() {
  const [keys, setKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");

  const loadKeys = () => api.get("/apikeys").then((r) => {
    setKeys(r.data.data);
    setSelectedKey((prev) => prev || r.data.data.find((k) => k.active)?.key || "");
  }).catch(() => {});
  useEffect(() => { loadKeys(); }, []);

  return (
    <div className="space-y-6" data-testid="api-page">
      <ApiKeysSection onChanged={loadKeys} />

      <div className="border border-stone-200 bg-white p-4" data-testid="api-usage-section">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#1C4E35]">Kullanım İstatistikleri</p>
        {keys.length === 0 ? (
          <p className="mt-2 text-xs text-stone-400">Henüz anahtar yok.</p>
        ) : (
          <table className="mt-2 w-full text-left text-xs">
            <thead>
              <tr className="border-b border-stone-200 text-[10px] uppercase tracking-wider text-stone-400">
                <th className="py-1.5 pr-3">Anahtar</th>
                <th className="py-1.5 pr-3">İstek Sayısı</th>
                <th className="py-1.5 pr-3">Son Kullanım</th>
                <th className="py-1.5">Oluşturulma</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="border-b border-stone-100" data-testid={`api-usage-row-${k.id}`}>
                  <td className="py-1.5 pr-3 font-medium text-[#0A0D0B]">{k.name}</td>
                  <td className="py-1.5 pr-3">{k.request_count || 0}</td>
                  <td className="py-1.5 pr-3">{k.last_used_at ? new Date(k.last_used_at).toLocaleString("tr-TR") : "—"}</td>
                  <td className="py-1.5">{k.created_at ? new Date(k.created_at).toLocaleString("tr-TR") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="border border-stone-200 bg-white p-4" data-testid="api-catalog-section">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#1C4E35]">
          <BookOpenText size={14} /> Endpoint Kataloğu
        </p>
        <p className="mt-1 text-xs text-stone-500">
          Tüm uçlar salt okunur GET'tir ve <code className="bg-stone-100 px-1">X-API-Key</code> başlığı ister. Base URL: <code className="bg-stone-100 px-1">{BASE}/api/ext/v1</code>
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-stone-600">Deneme / curl için anahtar:</span>
          <select value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)} className="h-8 rounded-sm border border-stone-300 bg-white px-2 text-xs" data-testid="api-key-select">
            <option value="">— Anahtar seçin —</option>
            {keys.map((k) => <option key={k.id} value={k.key}>{k.name}</option>)}
          </select>
        </div>
        <div className="mt-4 space-y-5">
          {ENDPOINTS.map((g) => (
            <div key={g.group}>
              <p className="mb-2 text-xs font-bold text-stone-700">{g.group}</p>
              <div className="space-y-2">
                {g.items.map((ep) => <EndpointCard key={ep.path} ep={ep} apiKey={selectedKey} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
